import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import { gambits, localize } from '@/core/gambits';
import { compressImage } from '@/core/helpers/image.helper';
import { NETWORK_ERROR_KEY } from '@/core/network/http/httpError';
import { ref } from 'vue';
import { useToast } from 'vue-toastification';

/**
 * A photo outlives the screen it was picked on: the handshake is offered and
 * the modal closes right away, while the upload carries on out here. State
 * lives at module level so whichever list is on screen can show the photo
 * arriving, and call the whole thing off, from wherever the upload began.
 */
const uploadingIds = ref<string[]>([]);
const controllers = new Map<string, AbortController>();

/**
 * The last upload to finish, however it finished — a cue to refetch. A fresh
 * object every time, so a second go at the same handshake is heard as well.
 */
const lastSettled = ref<{ handshakeId: string; at: number } | null>(null);

/** The API's key for a file already on one of this player's handshakes. */
const PHOTO_REPEATED_KEY = 'handshake_photo_repeated';

export function useHandshakePhotoUpload() {
  const toast = useToast();

  const isUploading = (handshakeId: string): boolean => uploadingIds.value.includes(handshakeId);

  const cancel = (handshakeId: string) => {
    controllers.get(handshakeId)?.abort();
  };

  /** A photo changed some other way, such as a delete. Every screen showing this handshake refetches. */
  const settle = (handshakeId: string) => {
    lastSettled.value = { handshakeId, at: Date.now() };
  };

  const upload = async (
    handshakeId: string,
    file: File | File[],
    positionIndex?: number,
  ): Promise<HandshakeDto | undefined> => {
    const files = (Array.isArray(file) ? file : [file]).filter((item) => !!item);
    if (!files.length || isUploading(handshakeId)) return;

    const controller = new AbortController();
    controllers.set(handshakeId, controller);
    uploadingIds.value = [...uploadingIds.value, handshakeId];
    let saved: HandshakeDto | undefined;
    let repeated: string | null = null;

    try {
      for (const item of files) {
        if (controller.signal.aborted) return saved;

        const photo = await compressImage(item);
        if (controller.signal.aborted) return saved;

        const result = await gambits.userService.addHandshakeImage(
          handshakeId,
          photo,
          controller.signal,
          positionIndex,
        );
        if (result.data) saved = result.data;

        // A file already on one of this player's handshakes is skipped, and the
        // rest of the selection still goes up. One word about it at the end.
        if (result.error?.errorKey === PHOTO_REPEATED_KEY) {
          repeated = result.error.message || null;
          continue;
        }

        // A photo nobody answered for is usually a photo that landed anyway, and
        // the refetch below brings it in. Only a server that turned it down is
        // worth interrupting anyone over. The rest of a selection stops there too.
        const wasRefused = !!result.error && result.error.errorKey !== NETWORK_ERROR_KEY;
        if (wasRefused && !controller.signal.aborted) {
          toast.error(result.error?.message || localize('handshake.photo.failed'));
          return saved;
        }
      }
      if (repeated && !controller.signal.aborted) toast.error(repeated);
    } finally {
      controllers.delete(handshakeId);
      uploadingIds.value = uploadingIds.value.filter((id) => id !== handshakeId);
      settle(handshakeId);
    }

    return saved;
  };

  return { uploadingIds, lastSettled, isUploading, upload, cancel, settle };
}
